import { Box, Stack } from "@mui/material";
import { Alert, Card, Typography } from "@platform/design-system";
import { useNotification } from "@platform/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { useCensusReadStatus } from "../hooks/useCensusReadStatus";
import { useCensusValidation } from "../hooks/useCensusValidation";
import {
  processingFailedMessage,
  readOutcomeMessage,
  readSucceeded,
} from "../outcome";

import { CensusLineChecks } from "./CensusLineChecks";
import { CensusPageHeading } from "./CensusPageHeading";
import { CensusRulesCard } from "./CensusRulesCard";
import { CensusUnreadColumns } from "./CensusUnreadColumns";
import { CensusUploadButton } from "./CensusUploadButton";
import { CensusValidationSkeleton } from "./CensusValidationSkeleton";
import { CensusReadingReport } from "./CensusReadingReport";

import type {
  AwaitedRead,
  CensusReadStatus,
} from "../hooks/useCensusReadStatus";
import type { CensusValidation } from "../hooks/useCensusValidation";

interface CensusValidationPanelProps {
  readonly rfpId: string;
  /** Named in the subtitle, so a reader knows whose census this is. */
  readonly clientName?: string;
}

/**
 * What the checklist will ask for once a census arrives.
 *
 * Shown instead of the checks themselves when there is no census to measure.
 * Rendering every field as missing would read as ten failures when nothing has
 * been uploaded, which is not a problem anybody can act on.
 */
const AwaitingCensus = ({
  validation,
}: {
  readonly validation: CensusValidation;
}): React.JSX.Element => (
  <Card sx={{ p: 2 }} data-testid="census-awaiting">
    <Stack gap={1}>
      <Typography variant="subtitle1">What this census will need</Typography>
      <Typography variant="body-medium" color="textSecondary">
        {validation.lines.length === 0
          ? "None of the coverages quoted on this RFP has a census checklist."
          : validation.lines.map((line) => line.line).join(" · ")}
      </Typography>
    </Stack>
  </Card>
);

/**
 * Says what a finished read made of the census.
 *
 * A read that produced nothing still finished, so this reports the outcome
 * rather than assuming success — an encrypted file and a parsed one both end
 * the wait.
 */
const useReadAnnouncement = (): ((
  status: CensusReadStatus | undefined,
) => void) => {
  const { showError, showSuccess } = useNotification();

  // Stable so the effect that calls it does not re-run every render.
  return useCallback(
    (status: CensusReadStatus | undefined) => {
      if (!status) {
        return;
      }

      const message = readOutcomeMessage(status.outcome);

      if (readSucceeded(status.outcome)) {
        showSuccess(message);

        return;
      }

      showError(message);
    },
    [showError, showSuccess],
  );
};

/** Says a read stopped being waited for, rather than leaving the page silent. */
const useGaveUpAnnouncement = (): (() => void) => {
  const { showError } = useNotification();

  return useCallback(
    () =>
      showError(
        "Reading this census is taking longer than expected. Refresh to check whether it finished.",
      ),
    [showError],
  );
};

/**
 * What census processing made of an RFP's census, measured against the
 * checklist for the coverages the RFP quotes.
 */
export const CensusValidationPanel = ({
  rfpId,
  clientName,
}: CensusValidationPanelProps): React.JSX.Element => {
  // Set when an upload is waiting to be read: the read the page is showing,
  // which the query polls past rather than settling on.
  const [awaitingRead, setAwaitingRead] = useState<AwaitedRead | undefined>();
  const announceRead = useReadAnnouncement();
  const announceGaveUp = useGaveUpAnnouncement();
  const queryClient = useQueryClient();

  const { validation, isLoading, isError } = useCensusValidation(rfpId);

  // Waiting is watched on the read status rather than the census: a read that
  // finds no members is never promoted, so the census cannot show it landing.
  const { status, gaveUp } = useCensusReadStatus(rfpId, awaitingRead);

  // Ending the wait and announcing it happen after commit, not during
  // render: announcing calls into the notification provider, and a render
  // may be replayed, which would report the same read twice.
  useEffect(() => {
    if (!awaitingRead) {
      return;
    }

    if (gaveUp) {
      setAwaitingRead(undefined);
      announceGaveUp();

      return;
    }

    // Absent until this upload's own document has been read. Another read
    // landing in the meantime is somebody else's and is not reported here.
    if (!status) {
      return;
    }

    // A read that fails also ends the wait, so what happened is reported
    // rather than assumed.
    setAwaitingRead(undefined);
    announceRead(status);

    void queryClient.invalidateQueries({
      queryKey: ["censusValidation", rfpId],
    });
  }, [
    awaitingRead,
    gaveUp,
    status,
    announceGaveUp,
    announceRead,
    queryClient,
    rfpId,
  ]);

  if (isLoading) {
    return <CensusValidationSkeleton />;
  }

  if (isError || !validation) {
    return (
      <Alert severity="error">
        Census validation could not be loaded for this RFP.
      </Alert>
    );
  }

  // A census that was read and produced nobody is a different problem from one
  // that was never sent, and the two need different words. `source` is set only
  // once reading has been attempted.
  const readingAttempted = validation.source !== undefined;
  const hasMembers = validation.members > 0;

  // The unread columns only answer something where a check failed. On a
  // census that passed everything they are noise.
  const anyCheckFailed = validation.lines.some(
    (line) => line.passed < line.total,
  );

  return (
    <Stack gap={3} data-testid="census-validation">
      <CensusPageHeading validation={validation} clientName={clientName} />

      {!readingAttempted && (
        <Alert severity="info">
          No census has been read for this RFP. One is read automatically when a
          broker submits an RFP with a census attached.
        </Alert>
      )}

      {validation.source && !hasMembers && (
        <Alert severity="warning">
          {processingFailedMessage(validation.source.outcome)}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          alignItems: "start",
        }}
      >
        {hasMembers ? (
          <Card sx={{ p: 2 }} data-testid="census-checks">
            <Stack gap={2}>
              {anyCheckFailed && (
                <CensusUnreadColumns headers={validation.unmappedHeaders} />
              )}

              {validation.lines.map((line) => (
                <CensusLineChecks
                  key={line.line}
                  line={line}
                  employees={validation.employees}
                />
              ))}
            </Stack>
          </Card>
        ) : (
          <AwaitingCensus validation={validation} />
        )}

        <Stack gap={2}>
          <CensusUploadButton
            rfpId={rfpId}
            hasCensus={hasMembers}
            onUploaded={(documentId) =>
              setAwaitingRead({ documentId, startedAt: Date.now() })
            }
          />

          {readingAttempted && <CensusReadingReport validation={validation} />}

          <CensusRulesCard lines={validation.lines} />
        </Stack>
      </Box>
    </Stack>
  );
};
